import { Clerk } from "@clerk/clerk-js";

function assertPublishableKey(value) {
  if (!value || typeof value !== "string") {
    throw new Error("vite_clerk_publishable_key is not set");
  }
  return value;
}

export function createClerkClient(publishableKey) {
  const key = assertPublishableKey(publishableKey);
  const clerk = new Clerk(key);
  let listeners = [];

  // base appearance for all clerk components
  const appearance = {
    baseTheme: undefined,
    variables: {
      colorPrimary: "#6f6f6f",
      colorBackground: "#151515",
      colorInputBackground: "#0f0f0f",
      colorText: "#ffffff",
      colorTextOnPrimaryBackground: "#ffffff",
      colorTextSecondary: "#8a8a8a",
      colorInputText: "#ffffff",
      colorNeutral: "#ffffff",
      colorDanger: "#b85450",
      colorSuccess: "#6b9b7a",
      colorWarning: "#b8853d",
      fontFamily: '"JetBrains Mono", "Monaco", "Consolas", monospace',
      borderRadius: "0px",
    },
    elements: {
      // root level
      rootBox: {
        color: "#ffffff",
      },
      card: {
        backgroundColor: "#151515",
        border: "none",
        boxShadow: "none",
        borderRadius: "0px",
        color: "#ffffff",
      },
      // user button popover
      userButtonPopoverCard: {
        backgroundColor: "#151515",
        border: "1px solid #3a3a3a",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.6)",
        borderRadius: "0px",
        color: "#ffffff",
      },
      userButtonTrigger: {
        borderRadius: "0px",
      },
      // avatars
      avatarBox: {
        borderRadius: "0px",
        border: "1px solid #3a3a3a",
      },
      avatarImage: {
        borderRadius: "0px",
      },
      // user profile modal
      modalContent: {
        backgroundColor: "#151515",
        border: "1px solid #3a3a3a",
        color: "#ffffff",
      },
      modalBackdrop: {
        backgroundColor: "rgba(0, 0, 0, 0.85)",
      },
      // navbar
      navbar: {
        backgroundColor: "#0f0f0f",
        borderRight: "1px solid #3a3a3a",
      },
      navbarButton: {
        color: "#8a8a8a",
      },
      navbarButtonIcon: {
        color: "#8a8a8a",
      },
      // page content
      scrollBox: {
        backgroundColor: "#151515",
      },
      pageScrollBox: {
        backgroundColor: "#151515",
      },
      page: {
        backgroundColor: "#151515",
        color: "#ffffff",
      },
      // profile sections
      profileSection: {
        borderBottom: "1px solid #3a3a3a",
      },
      profileSectionTitle: {
        color: "#ffffff",
        borderBottom: "1px solid #3a3a3a",
      },
      profileSectionTitleText: {
        color: "#ffffff",
      },
      profileSectionContent: {
        color: "#ffffff",
      },
      profileSectionPrimaryButton: {
        color: "#6f6f6f",
      },
      // user preview
      userPreview: {
        color: "#ffffff",
      },
      userPreviewMainIdentifier: {
        color: "#ffffff",
      },
      userPreviewSecondaryIdentifier: {
        color: "#8a8a8a",
      },
      // form elements
      formButtonPrimary: {
        backgroundColor: "#6f6f6f",
        borderColor: "#6f6f6f",
        color: "#ffffff",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
        borderRadius: "0px",
      },
      formFieldLabel: {
        color: "#ffffff",
      },
      formFieldInput: {
        backgroundColor: "#0f0f0f",
        borderColor: "#3a3a3a",
        color: "#ffffff",
        borderRadius: "0px",
      },
      // header - hidden for sign-in since we have our own branding
      header: {
        display: "none",
      },
      headerTitle: {
        display: "none",
      },
      headerSubtitle: {
        display: "none",
      },
      // footer - keep "don't have an account? sign up" but hide clerk badge
      footer: {
        color: "#8a8a8a",
      },
      footerAction: {
        color: "#8a8a8a",
      },
      footerActionLink: {
        color: "#6f6f6f",
      },
      footerPagesLink: {
        display: "none",
      },
      // badge
      badge: {
        backgroundColor: "#6f6f6f",
        color: "#ffffff",
        borderRadius: "0px",
      },
      // dividers
      dividerLine: {
        backgroundColor: "#3a3a3a",
      },
      dividerText: {
        color: "#8a8a8a",
      },
      // menu
      menuList: {
        backgroundColor: "#151515",
        border: "1px solid #3a3a3a",
      },
      menuItem: {
        color: "#8a8a8a",
      },
      // accordion
      accordionTriggerButton: {
        color: "#ffffff",
      },
      accordionContent: {
        color: "#ffffff",
      },
      // action buttons
      userButtonPopoverActionButton: {
        color: "#8a8a8a",
      },
      userButtonPopoverActionButtonIcon: {
        color: "#8a8a8a",
      },
      userButtonPopoverActionButtonText: {
        color: "#8a8a8a",
      },
      // text elements
      text: {
        color: "#ffffff",
      },
      providerIcon: {
        filter: "brightness(0) invert(1)",
      },
    },
  };

  async function load() {
    await clerk.load({
      appearance,
    });
    return clerk;
  }

  function isSignedIn() {
    return Boolean(clerk.isSignedIn);
  }

  function mountSignIn(node) {
    if (!node) return;
    clerk.mountSignIn(node, { appearance });
  }

  function mountUserButton(node) {
    if (!node) return;
    clerk.mountUserButton(node, { appearance });
  }

  function onAuthChange(callback) {
    if (typeof callback !== "function") {
      return () => {};
    }
    const wrapped = (event) => {
      try {
        callback(Boolean(event?.user), event || null);
      } catch (error) {
        console.error("clerk_listener_failed", {
          message: error instanceof Error ? error.message : String(error),
        });
      }
    };
    listeners.push(wrapped);
    clerk.addListener(wrapped);
    return () => {
      listeners = listeners.filter((fn) => fn !== wrapped);
      clerk.removeListener(wrapped);
    };
  }

  async function getAuthToken() {
    try {
      const session = clerk.session;
      if (!session) {
        return null;
      }
      return await session.getToken();
    } catch (error) {
      console.warn("clerk_token_failed", {
        message: error instanceof Error ? error.message : String(error),
      });
      return null;
    }
  }

  return {
    load,
    isSignedIn,
    mountSignIn,
    mountUserButton,
    onAuthChange,
    getAuthToken,
    raw: clerk,
  };
}
